import { formatMoney } from '@orgus/ui';
/**
 * The Missions page's UI model: board columns, filters, item trees, mission rows with their
 * derived view, and the Start mission form's checks. Pure, so it is unit tested.
 */
import { ITEM_KIND_LABEL, ITEM_STATUS_LABEL, MISSION_START_STEP_LABEL, approvalsForDerive, deriveMission, itemProgress } from '@orgus/protocol';
import type {
  ApprovalsState,
  BacklogItem,
  CanonicalSessionState,
  CostsState,
  Id,
  ItemKind,
  ItemStatus,
  Mission,
  MissionStartStep,
  MissionState,
  MissionView,
  MissionsState,
  Session,
} from '@orgus/protocol';
import type { StatusTone, Tone } from '@orgus/ui';
import type { Entities } from '../../lib/store.ts';
import { branchError } from '../new-session/validate.ts';

export const MISSIONS_EXT = 'missions';

// ---------------------------------------------------------------------------
// Tones
// ---------------------------------------------------------------------------

export function missionTone(state: MissionState): StatusTone {
  switch (state) {
    case 'planned':
      return 'queued';
    case 'running':
      return 'working';
    case 'waiting':
      return 'waiting';
    case 'done':
      return 'passed';
    case 'failed':
      return 'failed';
    case 'stopped':
      return 'ended';
  }
}

export function canonicalTone(state: CanonicalSessionState): StatusTone {
  switch (state) {
    case 'starting':
      return 'queued';
    case 'working':
      return 'working';
    case 'waiting_for_human':
      return 'waiting';
    case 'idle':
      return 'idle';
    case 'done':
      return 'passed';
    case 'failed':
      return 'failed';
    case 'stopped':
      return 'ended';
  }
}

export function itemStatusTone(status: ItemStatus): Tone {
  switch (status) {
    case 'triage':
      return 'neutral';
    case 'backlog':
      return 'neutral';
    case 'ready':
      return 'queued';
    case 'in_progress':
      return 'working';
    case 'review':
      return 'waiting';
    case 'done':
      return 'passed';
    case 'dropped':
      return 'ended';
  }
}

// ---------------------------------------------------------------------------
// Backlog
// ---------------------------------------------------------------------------

/** The board's columns, left to right. Triage shows only when something is in it; dropped items show only in the table. */
export const BOARD_COLUMNS: readonly ItemStatus[] = ['triage', 'backlog', 'ready', 'in_progress', 'review', 'done'];

export interface BacklogFilter {
  projectId?: Id | null;
  kind?: ItemKind | null;
  /** Free text over title, body, owner and id. */
  q?: string;
  showDropped?: boolean;
}

export function matchesItem(i: BacklogItem, f: BacklogFilter): boolean {
  if (f.projectId && i.projectId !== f.projectId) return false;
  if (f.kind && i.kind !== f.kind) return false;
  if (!f.showDropped && i.status === 'dropped') return false;
  const q = f.q?.trim().toLowerCase();
  if (q && !`${i.title} ${i.body} ${i.owner ?? ''} ${i.id} ${ITEM_KIND_LABEL[i.kind]}`.toLowerCase().includes(q)) return false;
  return true;
}

/** Urgent first, then most recently changed. */
export function byPriority(a: BacklogItem, b: BacklogItem): number {
  return a.priority - b.priority || b.updatedAt - a.updatedAt || a.id.localeCompare(b.id);
}

export function filterItems(items: readonly BacklogItem[], f: BacklogFilter): BacklogItem[] {
  return items.filter((i) => matchesItem(i, f)).sort(byPriority);
}

export interface BoardColumn {
  status: ItemStatus;
  title: string;
  items: BacklogItem[];
}

export function boardColumns(items: readonly BacklogItem[], f: BacklogFilter): BoardColumn[] {
  const shown = filterItems(items, { ...f, showDropped: false });
  return BOARD_COLUMNS.map((status) => ({ status, title: ITEM_STATUS_LABEL[status], items: shown.filter((i) => i.status === status) })).filter((c) => c.status !== 'triage' || c.items.length > 0);
}

export function childrenOf(items: readonly BacklogItem[], id: Id): BacklogItem[] {
  return items.filter((i) => i.parentId === id).sort(byPriority);
}

/** Every item under `id`, at any depth. */
export function descendantsOf(items: readonly BacklogItem[], id: Id): Set<Id> {
  const out = new Set<Id>();
  const walk = (pid: Id) => {
    for (const c of items) if (c.parentId === pid && !out.has(c.id)) (out.add(c.id), walk(c.id));
  };
  walk(id);
  return out;
}

/** Items that may be the parent of `selfId` in `projectId`: same project, not itself or below it, not a chore or bug. */
export function parentCandidates(items: readonly BacklogItem[], projectId: Id, selfId?: Id | null): BacklogItem[] {
  const below = selfId ? descendantsOf(items, selfId) : new Set<Id>();
  return items
    .filter((i) => i.projectId === projectId && i.id !== selfId && !below.has(i.id) && (i.kind === 'epic' || i.kind === 'story') && i.status !== 'dropped')
    .sort((a, b) => Number(a.kind !== 'epic') - Number(b.kind !== 'epic') || a.title.localeCompare(b.title));
}

export function progressOf(item: BacklogItem, items: readonly BacklogItem[]) {
  return itemProgress(item, items);
}

// ---------------------------------------------------------------------------
// Missions
// ---------------------------------------------------------------------------

export interface MissionRow {
  mission: Mission;
  view: MissionView;
  item: BacklogItem | null;
}

/** The parts of the store a mission view reads. */
export type DeriveSource = Pick<Entities, 'sessions' | 'machines' | 'ext'>;

export function missionsSlice(ext: Record<string, unknown>): MissionsState | null {
  const s = ext[MISSIONS_EXT] as MissionsState | undefined;
  return s && Array.isArray(s.items) && Array.isArray(s.missions) ? s : null;
}

/** Items by id, built once per items array (every mission view reads its item). */
const itemIndexes = new WeakMap<readonly BacklogItem[], Map<Id, BacklogItem>>();
function itemIndex(items: readonly BacklogItem[] | undefined): Map<Id, BacklogItem> | undefined {
  if (!items) return undefined;
  let map = itemIndexes.get(items);
  if (!map) itemIndexes.set(items, (map = new Map(items.map((i) => [i.id, i]))));
  return map;
}

/**
 * Every session as a list, built once per sessions map. Each mission's view took
 * Object.values of the whole map: with 10,000 sessions that was 200 ms for the missions on
 * every Overview open and live update (22 Oct 2026).
 */
const sessionLists = new WeakMap<object, Session[]>();
export function sessionList(sessions: DeriveSource['sessions']): Session[] {
  let list = sessionLists.get(sessions);
  if (!list) sessionLists.set(sessions, (list = Object.values(sessions) as Session[]));
  return list;
}

/** Derive a mission against live store data: sessions, machines, and the approvals and costs modules when present. */
export function viewOf(m: Mission, src: DeriveSource, now: number): MissionView {
  const approvals = src.ext.approvals as Partial<ApprovalsState> | undefined;
  const costs = src.ext.costs as Partial<CostsState> | undefined;
  const items = itemIndex(missionsSlice(src.ext)?.items);
  return deriveMission(m, {
    // ADR 0078: judge the final report against the item's criteria, and wait for the hub to read it, as the hub does.
    item: (id) => items?.get(id),
    readsReports: true,
    sessions: (id) => src.sessions[id],
    allSessions: sessionList(src.sessions),
    approvals: approvalsForDerive(approvals), // pending plus blocking: an expired or denied approval never reads as done (R-HL-3)
    costs: Array.isArray(costs?.sessions) ? { sessions: costs.sessions } : null,
    machineOnline: (id) => src.machines[id]?.online ?? false,
    now,
  });
}

export function missionRows(state: MissionsState, src: DeriveSource, now: number): MissionRow[] {
  const items = new Map(state.items.map((i) => [i.id, i]));
  return state.missions.map((mission) => ({ mission, view: viewOf(mission, src, now), item: mission.itemId ? items.get(mission.itemId) ?? null : null }));
}

export type MissionScope = 'active' | 'closed' | 'all';

export function isActive(state: MissionState): boolean {
  return state === 'planned' || state === 'running' || state === 'waiting';
}

/** Needs-you first, then running, then planned; closed ones by most recent. */
const STATE_RANK: Record<MissionState, number> = { waiting: 0, running: 1, planned: 2, failed: 3, done: 4, stopped: 5 };

/**
 * Open on the Missions tab: under way, or finished with its item waiting for review. The tab's count then agrees with the Needs you and In review tiles (R-FN-8).
 */
export function isOpenRow(r: Pick<MissionRow, 'view' | 'item'>): boolean {
  return isActive(r.view.state) || (r.view.state === 'done' && r.item?.status === 'review');
}

export function filterMissions(rows: readonly MissionRow[], scope: MissionScope, projectId?: Id | null): MissionRow[] {
  return rows
    .filter((r) => (!projectId || r.mission.projectId === projectId) && (scope === 'all' || (scope === 'active') === isOpenRow(r)))
    .sort((a, b) => STATE_RANK[a.view.state] - STATE_RANK[b.view.state] || b.mission.updatedAt - a.mission.updatedAt);
}

export function missionsOfItem(rows: readonly MissionRow[], itemId: Id): MissionRow[] {
  return rows.filter((r) => r.mission.itemId === itemId).sort((a, b) => b.mission.createdAt - a.mission.createdAt);
}

/** The most relevant mission of an item for its card: an active one, else the newest. */
export function headlineMission(rows: readonly MissionRow[], itemId: Id): MissionRow | undefined {
  const mine = missionsOfItem(rows, itemId);
  return mine.find((r) => isActive(r.view.state)) ?? mine[0];
}

export function scopeCounts(rows: readonly MissionRow[], projectId?: Id | null): Record<MissionScope, number> {
  const c = { active: 0, closed: 0, all: 0 };
  for (const r of rows) {
    if (projectId && r.mission.projectId !== projectId) continue;
    c.all++;
    if (isOpenRow(r)) c.active++;
    else c.closed++;
  }
  return c;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** Dollars by the shared money format: "$1.20", "$1,284", "$19.1K". */
export function formatUsd(n: number): string {
  return formatMoney(n);
}

/** "$1.20 of $5.00" or "$1.20" or "No budget". */
export function spendLabel(cost: number | null, budget: number | null): string {
  if (cost == null) return budget != null ? `Budget ${formatUsd(budget)}` : 'Not tracked';
  return budget != null ? `${formatUsd(cost)} of ${formatUsd(budget)}` : formatUsd(cost);
}

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

export interface StartForm {
  title: string;
  goal: string;
  projectId: Id;
  machineId: Id;
  where: 'checkout' | 'worktree';
  checkoutId: Id;
  branch: string;
  budget: string;
  note: string;
}

export interface StartFormContext {
  machineOnline: (id: Id) => boolean;
  machineName: (id: Id) => string | undefined;
  /** Checkouts of the project on the chosen machine. */
  checkouts: { id: Id; kind: 'main' | 'worktree'; branch: string | null }[];
  /** Whether the dialog creates the mission (title and goal editable) or starts an existing one. */
  creating: boolean;
}

export type StartFormErrors = Partial<Record<'title' | 'goal' | 'project' | 'machine' | 'checkout' | 'branch' | 'budget' | 'note', string>>;

/** What the budget field does at its amount: a warning on the mission, never a stop. */
export function budgetHint(costsOn: boolean, hardStop = false): string {
  if (hardStop) return costsOn ? 'Orgus stops the mission once it spends more.' : 'Orgus stops the mission once it spends more, when cost tracking is on.';
  return costsOn ? 'Warns on the mission at this amount. Sessions keep running.' : 'Warns at this amount once cost tracking is on. Sessions keep running.';
}

/** Parse a budget field: '' is no budget; anything else must be a positive amount. */
export function parseBudget(raw: string): { value: number | null; error?: string } {
  const t = raw.trim().replace(/^\$/, '');
  if (!t) return { value: null };
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return { value: null, error: 'Enter a positive amount in USD, or leave it empty for no budget.' };
  if (n > 1e6) return { value: null, error: 'That budget is over $1,000,000. Enter a smaller amount.' };
  return { value: Math.round(n * 100) / 100 };
}

export function startFormErrors(f: StartForm, ctx: StartFormContext): StartFormErrors {
  const e: StartFormErrors = {};
  if (ctx.creating) {
    if (!f.title.trim()) e.title = 'Give the mission a title.';
    else if (f.title.length > 200) e.title = 'Keep the title under 200 characters.';
    if (!f.goal.trim()) e.goal = 'Say what done looks like. The goal goes into the prompt.';
    else if (f.goal.length > 4000) e.goal = 'Keep the goal under 4,000 characters.';
  }
  if (!f.projectId) e.project = 'Choose a project.';
  if (!f.machineId) e.machine = 'Choose a machine with a checkout of this project.';
  else if (!ctx.machineOnline(f.machineId)) e.machine = `${ctx.machineName(f.machineId) ?? 'That machine'} is offline. Choose another.`;
  const base = ctx.checkouts.find((c) => c.kind === 'main') ?? ctx.checkouts[0];
  if (f.where === 'checkout' && !ctx.checkouts.some((c) => c.id === f.checkoutId)) e.checkout = 'Choose a checkout.';
  if (f.where === 'worktree') {
    if (!base) e.checkout = 'No checkout to branch from on this machine.';
    const b = branchError(f.branch, ctx.checkouts.map((c) => c.branch).filter((x): x is string => !!x));
    if (b) e.branch = b;
  }
  const budget = parseBudget(f.budget);
  if (budget.error) e.budget = budget.error;
  if (f.note.length > 4000) e.note = 'Keep the note under 4,000 characters.';
  return e;
}

/**
 * What Start mission is doing, in words, while it runs: saving the mission, then the step the
 * hub reports. A hub that reports none (older, or the demo) gets the first step the start takes.
 */
export function startStepText(phase: 'saving' | 'starting' | null, hubStep: MissionStartStep | undefined, where: StartForm['where']): string | null {
  if (phase === 'saving') return 'Saving mission';
  if (phase !== 'starting') return null;
  return MISSION_START_STEP_LABEL[hubStep ?? (where === 'worktree' ? 'worktree' : 'launching')];
}

/**
 * A goal to start from, for an item: a one-line outcome from its title. The description and
 * the acceptance criteria already follow in the prompt, so the goal never repeats them.
 */
export function goalFromItem(item: Pick<BacklogItem, 'title' | 'kind'> & Partial<Pick<BacklogItem, 'acceptance'>>): string {
  const title = item.title.trim().replace(/[.\s]+$/, '');
  const base = item.kind === 'defect' ? `Fix: ${title}` : title;
  return item.acceptance?.length ? `${base}. Done when every acceptance criterion is met.` : base;
}

/** A branch suggestion for a mission: mission/<words-of-title>. */
export function branchFromTitle(title: string): string {
  const words = title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .map((w) => w.replace(/-+/g, '-').replace(/^-|-$/g, ''))
    .filter(Boolean)
    .slice(0, 5);
  return words.length ? `mission/${words.join('-')}`.slice(0, 48).replace(/-+$/, '') : '';
}

// ---------------------------------------------------------------------------
// Review
// ---------------------------------------------------------------------------

/** When a mission finished: when it was closed or its branch landed, else when it last changed. */
export function finishedAt(m: Pick<Mission, 'closed' | 'landed' | 'updatedAt'>): number {
  return m.closed?.at ?? m.landed?.at ?? m.updatedAt;
}

/**
 * The review queue: each item a finished mission sent to review (its newest mission is done and
 * the item waits In review), oldest first, so the queue clears in the order work arrived.
 */
export function reviewQueue(rows: readonly MissionRow[], projectId?: Id | null): MissionRow[] {
  const newest = new Map<Id, MissionRow>();
  for (const r of rows) {
    if (!r.item) continue;
    const cur = newest.get(r.item.id);
    if (!cur || r.mission.createdAt > cur.mission.createdAt) newest.set(r.item.id, r);
  }
  return [...newest.values()]
    .filter((r) => r.view.state === 'done' && r.item!.status === 'review' && (!projectId || r.mission.projectId === projectId))
    .sort((a, b) => finishedAt(a.mission) - finishedAt(b.mission) || a.mission.id.localeCompare(b.mission.id));
}
